import { useEffect, useState } from 'react'
import { FACTORY_ADDRESS } from '@icecreamswap/constants/src/common/swap'
import FactoryABI from 'config/abi/Factory.json'
import PancakePairABI from 'config/abi/IPancakePair.json'
import LpTokenABI from 'config/abi/lpToken.json'
import useActiveWeb3React from './useActiveWeb3React'
import { ethers } from 'ethers'

const burnAddress = '0x000000000000000000000000000000000000dead'

export const useLpTokenStatus = (tokenAddress: string | null, pairedTokenAddress: string | null) => {
  const { provider } = useActiveWeb3React()
  const [lpTokenAddress, setLpTokenAddress] = useState<string | null>(null)
  const [burnBalance, setBurnBalance] = useState<string | null>(null)
  const [totalSupply, setTotalSupply] = useState<string | null>(null)
  const [allBurned, setAllBurned] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(true) 

  useEffect(() => {
    if (!tokenAddress || !pairedTokenAddress || !provider) {
      setLoading(false)
      return
    }

    const fetchAllPairs = async () => {
      const factoryContract = new ethers.Contract(FACTORY_ADDRESS, FactoryABI, provider)
      const pairCount = await factoryContract.allPairsLength()
      const pairPromises = []

      for (let i = 0; i < pairCount; i++) {
        pairPromises.push(factoryContract.allPairs(i))
      }

      return Promise.all(pairPromises)
    }

    const findTokenPair = async (pairs: string[]) => {
      const tokenPromises = pairs.map(async (pairAddress) => {
        const pairContract = new ethers.Contract(pairAddress, PancakePairABI, provider)
        const [token0, token1] = await Promise.all([pairContract.token0(), pairContract.token1()])
        return { pairAddress, token0, token1 }
      })

      const tokenResults = await Promise.all(tokenPromises)

      for (const { pairAddress, token0, token1 } of tokenResults) {
        if (
          (token0.toLowerCase() === tokenAddress.toLowerCase() &&
            token1.toLowerCase() === pairedTokenAddress.toLowerCase()) ||
          (token1.toLowerCase() === tokenAddress.toLowerCase() &&
            token0.toLowerCase() === pairedTokenAddress.toLowerCase())
        ) {
          setLpTokenAddress(pairAddress)
          return pairAddress
        }
      }
      return null
    }

    const checkLPBurned = async (lpAddress: string) => {
      const lpTokenContract = new ethers.Contract(lpAddress, LpTokenABI, provider)
      const [balance, supply] = await Promise.all([
        lpTokenContract.balanceOf(burnAddress),
        lpTokenContract.totalSupply(),
      ])

      setBurnBalance(balance.toString())
      setTotalSupply(supply.toString())

      if (balance.eq(supply)) {
        setAllBurned(true)
      }
    }

    const main = async () => {
      setLoading(true)
      try {
        const pairs = await fetchAllPairs()
        const lpAddress = await findTokenPair(pairs)
        if (lpAddress) {
          await checkLPBurned(lpAddress)
        } else {
          console.log('No LP token found for the specified token.')
        }
      } catch (error) {
        console.error('Error fetching LP token status:', error)
      } finally {
        setLoading(false)
      }
    }

    main()
  }, [provider, tokenAddress, pairedTokenAddress])

  return { lpTokenAddress, burnBalance, totalSupply, allBurned, loading }
}
